import { Languages } from "@tree-company/collect-js-client";
import translate from "./locales";
import { THEMES } from "./themes";
import { Redirect } from "./components/redirect";
import { TellitPage } from "./components/tellit_page";
import { PageComponent } from "./components/page_component";
import { client } from "./app";
import { CollapsibleRadio } from "@tree-company/mjp-shared";

let aggregatorURL = import.meta.env.VITE_AGGREGATOR_URL;
if (aggregatorURL === undefined && import.meta.env.DEV) {
  aggregatorURL = "http://localhost:3300";
}

function translatedPropertyFromKey(translation_key, interpolations) {
  return {
    en: translate(translation_key, interpolations, Languages.en),
  };
}

function getPreviousAnswer(key) {
  // We select the second to last one, since the last treesponse is the current one
  const treesponses_length = client.treesponses.length;
  if (treesponses_length >= 2) {
    return client.treesponses[treesponses_length - 2]?.getAnswer(key);
  }
}

const aiResponseTexts = {
  proficiencyOkToxicityOk: translatedPropertyFromKey(
    "ai_feedback.proficiency_ok_toxicity_ok",
  ),
  proficiencyOkToxicityNotOk: translatedPropertyFromKey(
    "ai_feedback.proficiency_ok_toxicity_not_ok",
  ),
  proficiencyHighToxicityOk: translatedPropertyFromKey(
    "ai_feedback.proficiency_high_toxicity_ok",
  ),
  proficiencyHighToxicityNotOk: translatedPropertyFromKey(
    "ai_feedback.proficiency_high_toxicity_not_ok",
  ),
  proficiencyLowToxicityOk: translatedPropertyFromKey(
    "ai_feedback.proficiency_low_toxicity_ok",
  ),
  proficiencyLowToxicityNotOk: translatedPropertyFromKey(
    "ai_feedback.proficiency_low_toxicity_not_ok",
  ),
};

const namePage = {
  key: "name",
  order: 4,
  component: PageComponent,
  skipIf: (treesponse) => {
    const comment = treesponse.getAnswer("comment");
    return typeof comment !== "string" || comment.length === 0;
  },
  description: translatedPropertyFromKey(["name", "intro", "description"]),
  sections: [
    {
      order: 1,
      type: "string",
      key: "firstName",
      title: translatedPropertyFromKey(["name", "firstName", "title"]),
      required: true,
      defaultValue: () => getPreviousAnswer("firstName"),
    },
    {
      order: 2,
      type: "string",
      key: "lastName",
      title: translatedPropertyFromKey(["name", "lastName", "title"]),
      required: true,
      defaultValue: () => getPreviousAnswer("lastName"),
    },
    {
      order: 3,
      type: "hidden",
      key: "completed",
      defaultValue: true,
    },
  ],
};

const sociodemoPage = {
  key: "sociodemo",
  order: 5,
  component: PageComponent,
  title: translatedPropertyFromKey(["sociodemo", "intro", "title"]),
  description: translatedPropertyFromKey(["sociodemo", "intro", "description"]),
  // We skip this page if the user answered the same name as their previous answer
  skipIf: (treesponse) => {
    return ["firstName", "lastName"].every(
      (key) => treesponse.getAnswer(key) === getPreviousAnswer(key),
    );
  },
  sections: [
    {
      order: 1,
      type: "radio",
      component: CollapsibleRadio,
      key: "gender",
      title: translatedPropertyFromKey(["sociodemo", "gender", "title"]),
      options: ["m", "v", "x"].map((value) => ({
        label: translatedPropertyFromKey([
          "sociodemo",
          "gender",
          "options",
          value,
        ]),
        value,
      })),
      defaultValue: () => getPreviousAnswer("gender"),
    },
    {
      order: 1,
      type: "number",
      key: "yearOfBirth",
      title: translatedPropertyFromKey(["sociodemo", "year_of_birth", "title"]),
      options: [],
      defaultValue: () => getPreviousAnswer("year_of_birth"),
      maxLength: 4,
      placeholder: translatedPropertyFromKey([
        "sociodemo",
        "year_of_birth",
        "placeholder",
      ]),
    },
  ],
};

const likePage = {
  key: "like",
  order: 6,
  component: PageComponent,
  sections: [
    {
      type: "like",
      order: 1,
      key: "like",
      title: translatedPropertyFromKey(["like", "title"]),
      description: translatedPropertyFromKey(["like", "description"]),
      aggregatorSettings: {
        url: `${aggregatorURL}/mjp/demo-bpact`,
        projectKey: import.meta.env.VITE_AGGREGATOR_KEY,
      },
    },
  ],
};

const optinPage = {
  key: "optin",
  order: 7,
  component: PageComponent,
  title: translatedPropertyFromKey(["optin", "intro", "title"]),
  // We only show the opt in page if the user has not encountered it before
  skipIf: () => client.treesponses.some((t) => t.page?.name === "completed"),
  sections: [
    {
      type: "checkbox",
      order: 1,
      title: translatedPropertyFromKey(["optin", "newsletter", "title"]),
      description: translatedPropertyFromKey([
        "optin",
        "newsletter",
        "description",
      ]),
      key: "newsletter",
    },
    {
      type: "email",
      order: 2,
      skipIf: (treesponse) => treesponse.getAnswer("newsletter") !== true,
      dependsOn: ["newsletter"],
      title: translatedPropertyFromKey(["optin", "email", "title"]),
      key: "email",
      customValidations: [
        (treesponse, _key, value) => {
          if (
            treesponse.getAnswer("newsletter") &&
            (value === null || value === undefined)
          ) {
            return [
              false,
              translatedPropertyFromKey(["optin", "email", "required"]),
            ];
          }

          return [true, null];
        },
      ],
    },
  ],
};

const redirectPage = {
  key: "completed",
  order: 8,
  component: Redirect,
  sections: [],
};

export function surveySettingsForTheme(theme) {
  return {
    key: `mjp-lennik-${theme}`,
    pages: [
      {
        key: "tellit",
        order: 1,
        component: TellitPage,
        sections: [
          {
            type: "checkboxes",
            htmlAttributes: { class: "question--tellit" },
            order: 1,
            key: "actions",
            maxSelected: 2,
            title: translatedPropertyFromKey([theme, "actions", "title"]),
            description: translatedPropertyFromKey([
              theme,
              "actions",
              "description",
            ]),
            options: THEMES[theme].map((action) => ({
              label: translatedPropertyFromKey([
                theme,
                "actions",
                "options",
                action,
              ]),
              value: action,
              ariaDisabled: (t) => {
                const answer = t.getAnswer("actions") || [];
                return answer.length >= 2 && !answer.includes(action);
              },
            })),
          },
        ],
      },
      {
        key: `comment-${theme}`,
        order: 3,
        component: PageComponent,
        sections: [
          {
            order: 1,
            type: "text",
            key: "comment",
            maxLength: 400,
            title: translatedPropertyFromKey([
              theme,
              "comment_question",
              "title",
            ]),
            placeholder: translatedPropertyFromKey([
              theme,
              "comment_question",
              "placeholder",
            ]),
            aiResponseTexts,
          },
        ],
      },
      namePage,
      sociodemoPage,
      likePage,
      optinPage,
      redirectPage,
    ],
  };
}
